import React, { useState, useEffect, useCallback } from 'react';
import { CheckSquare, Plus, AlertTriangle, Eye, EyeOff, Flag, ChevronDown, ChevronUp } from 'lucide-react';
import { ActionItemModal, ActionItem } from './ActionItemModal';

interface ActionItemStats {
  total: number;
  not_started: number;
  in_progress: number;
  completed: number;
  overdue: number;
  due_soon: number;
}

interface ActionItemsPanelProps {
  clientId: string;
  clientName: string;
}

/**
 * ACTION ITEMS PANEL (Simplified)
 *
 * Displays action items/todos for a client in the right sidebar.
 * Coach view - simplified list showing titles only.
 *
 * Design principles:
 * - Title only in list view (details in modal)
 * - Yellow title = due within 3 days
 * - Red title = overdue
 * - No quick-complete (coach shouldn't casually close client tasks)
 * - Click to open detail modal
 * - Sorted by: priority → due date → alphabetical (backend handles this)
 */
export const ActionItemsPanel: React.FC<ActionItemsPanelProps> = ({
  clientId,
  clientName
}) => {
  const [items, setItems] = useState<ActionItem[]>([]);
  const [stats, setStats] = useState<ActionItemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ActionItem | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);

  // Collapse state - persist to localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('actionItemsCollapsed');
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('actionItemsCollapsed', String(isCollapsed));
  }, [isCollapsed]);

  const fetchActionItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/clients/${clientId}/action-items?include_completed=true`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch action items');
      }

      const data = await response.json();
      setItems(data.items || []);
      setStats(data.stats || null);
      setError(null);
    } catch (err) {
      console.error('Error fetching action items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load action items');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchActionItems();
  }, [fetchActionItems]);

  const handleItemClick = (item: ActionItem) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCreateNew = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleModalSave = () => {
    fetchActionItems();
    handleModalClose();
  };

  // Determine due date status for title coloring
  const getDueDateStatus = (dueDate: string | null, status: string): 'normal' | 'due-soon' | 'overdue' | 'completed' => {
    if (status === 'completed') return 'completed';
    if (!dueDate) return 'normal';

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const daysUntilDue = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= 3) return 'due-soon';
    return 'normal';
  };

  if (loading) {
    return (
      <div className={`card action-items-panel ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="section-header" onClick={() => setIsCollapsed(!isCollapsed)}>
          <h3><CheckSquare size={16} /> Action Items</h3>
          <button className="collapse-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
        {!isCollapsed && (
          <div className="loading-state">
            <span className="loading-spinner" />
            <span>Loading...</span>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`card action-items-panel ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="section-header" onClick={() => setIsCollapsed(!isCollapsed)}>
          <h3><CheckSquare size={16} /> Action Items</h3>
          <button className="collapse-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
        {!isCollapsed && (
          <div className="error-state">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  // Filter items based on completion status
  const activeItems = items.filter(item => item.status !== 'completed');
  const completedItems = items.filter(item => item.status === 'completed');
  const completedCount = completedItems.length;

  // Items to display in the list
  const displayItems = showCompleted ? [...activeItems, ...completedItems] : activeItems;

  return (
    <div className={`card action-items-panel ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="section-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <h3>
          <CheckSquare size={16} /> Action Items
          {activeItems.length > 0 && (
            <span className="count">({activeItems.length})</span>
          )}
        </h3>
        <div className="header-buttons">
          <button
            className="btn-icon"
            onClick={(e) => { e.stopPropagation(); handleCreateNew(); }}
            title="Add action item"
          >
            <Plus size={14} />
          </button>
          <button className="collapse-btn" title={isCollapsed ? 'Expand' : 'Collapse'}>
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>

      {!isCollapsed && (displayItems.length === 0 ? (
        <div className="empty-state">
          <p>No action items</p>
          <button className="btn btn-sm btn-primary" onClick={handleCreateNew}>
            <Plus size={14} /> Add Task
          </button>
        </div>
      ) : (
        <div className="action-items-list">
          {/* Active items */}
          {activeItems.map((item) => {
            const dueDateStatus = getDueDateStatus(item.due_date, item.status);
            const flagColorClass = `flag-${item.priority}`;
            const rowClasses = [
              'action-item-row',
              dueDateStatus !== 'normal' ? `row-${dueDateStatus}` : ''
            ].filter(Boolean).join(' ');

            return (
              <div
                key={item.id}
                className={rowClasses}
                onClick={() => handleItemClick(item)}
              >
                <Flag size={14} className={`action-item-flag ${flagColorClass}`} />
                <span className="action-item-title">{item.title}</span>
              </div>
            );
          })}
          {/* Divider and completed items */}
          {showCompleted && completedItems.length > 0 && (
            <>
              <div className="action-items-divider">
                <span>Completed</span>
              </div>
              {completedItems.map((item) => (
                <div
                  key={item.id}
                  className="action-item-row"
                  onClick={() => handleItemClick(item)}
                >
                  <Flag size={14} className={`action-item-flag flag-${item.priority} flag-completed`} />
                  <span className="action-item-title completed">
                    {item.title}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      ))}

      {/* Stats bar - show stats and completed toggle */}
      {!isCollapsed && (stats && (stats.overdue > 0 || stats.due_soon > 0 || completedCount > 0)) && (
        <div className="action-items-stats">
          <div className="stats-left">
            {stats.overdue > 0 && (
              <span className="stat overdue">
                <AlertTriangle size={12} /> {stats.overdue} overdue
              </span>
            )}
            {stats.due_soon > 0 && (
              <span className="stat due-soon">
                {stats.due_soon} due soon
              </span>
            )}
          </div>
          {completedCount > 0 && (
            <button
              className={`show-completed-btn ${showCompleted ? 'active' : ''}`}
              onClick={() => setShowCompleted(!showCompleted)}
              title={showCompleted ? 'Hide completed' : 'Show completed'}
            >
              {showCompleted ? <EyeOff size={12} /> : <Eye size={12} />}
              <span>{completedCount} done</span>
            </button>
          )}
        </div>
      )}

      {showModal && (
        <ActionItemModal
          clientId={clientId}
          clientName={clientName}
          item={editingItem}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

export default ActionItemsPanel;
